import { EventType } from "@/analytics/analytics-events";
import { AuthorizeExternalAnnotationProperties } from "@/analytics/generic-events-properties";
import { parseErrorMessage } from "@/errors/components/error-handling-context";
import { selectProjectIdForIntegrationType } from "@/store/integrations/integrations-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  blue,
  FaroStatusText,
  FaroText,
  FilterMenuOptionType,
  FilterMenuOptionTypeWithHeader,
  green,
  grey,
  NO_TRANSLATE_CLASS,
  yellow,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { AnnotationStatus } from "@faro-lotv/ielement-types";
import {
  ApiResponseError,
  BcfServicesIntegrationType,
  isBcfBackendApiErrorDetails,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { ProjectTopicStatusExtensionItem } from "@faro-lotv/service-wires/dist/bcf-services-api/bcf-services-api-responses";
import { Skeleton, Stack } from "@mui/material";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";
import {
  getKeyForHybridStatus,
  HybridAnnotationStatusKey,
} from "../../annotation-panel/annotation-panel-selector";
import { ExternalAnnotationError } from "../../annotations/external-annotation-error";
import { getExternalAnnotationProviderName } from "../../annotations/external-annotation-utils";
import { ChipFilterWithSearch } from "./chip-filter-with-search";

export type AnnotationStatusFilterProps = {
  /** List of selected status keys. */
  selectedStatus: HybridAnnotationStatusKey[];

  /** Callback executed when the selection changes */
  onSelectedStatusChanged(statuses: HybridAnnotationStatusKey[]): void;
};

/**
 * @returns the display name for a specific type of provider
 * @param externalAnnotationType type of the external annotation service
 */
function getProviderName(
  externalAnnotationType: BcfServicesIntegrationType,
): string {
  switch (externalAnnotationType) {
    case BcfServicesIntegrationType.autodeskAccIssues:
      return "ACC Issues";
    case BcfServicesIntegrationType.autodeskAccRfis:
      return "ACC RFI";
    case BcfServicesIntegrationType.procoreObservations:
      return "Procore Observation";
    case BcfServicesIntegrationType.procoreRfis:
      return "Procore RFI";
    case BcfServicesIntegrationType.autodeskBim360Issues:
      return "BIM 360 Issues";
    case BcfServicesIntegrationType.autodeskBim360Rfis:
      return "BIM 360 RFI";
  }
}

/**
 * @returns the optional list of Option components (including the header) representing the status for a specific type of external annotation
 * @param externalAnnotationType type of the external annotation service
 */
function useGetExternalStatusList(
  externalAnnotationType: BcfServicesIntegrationType,
): FilterMenuOptionTypeWithHeader[] {
  /**  Describe the current known status for a specific provider. */
  type ProjectExtensionStatusType = {
    /**
     * Current state of the request:
     * "pending" = request still running
     * "error" = we received an error from the provider stored in error
     * "completed" = we received the final list stored in statusList
     */
    pendingStatus: "pending" | "error" | "completed";
    /** Optional description of the error in case state is "error". */
    error?: ReactNode;
    /** List of status once they are known (i.e. when state is "completed") */
    statusList: ProjectTopicStatusExtensionItem[];
  };
  const [projectExtensionsStatus, setProjectExtensionsStatus] =
    useState<ProjectExtensionStatusType>({
      pendingStatus: "completed",
      statusList: [],
    });

  const { bcfServicesApiClient } = useApiClientContext();

  const projectId = useAppSelector(
    selectProjectIdForIntegrationType(externalAnnotationType),
  );

  const getProjectExtensions = useCallback(async (): Promise<void> => {
    if (!projectId) {
      setProjectExtensionsStatus({
        pendingStatus: "completed",
        statusList: [],
      });
      return;
    }
    setProjectExtensionsStatus({
      pendingStatus: "pending",
      statusList: [],
    });
    try {
      const { topic_status: topicStatus } =
        await bcfServicesApiClient.getProjectExtensions(
          externalAnnotationType,
          projectId,
        );
      setProjectExtensionsStatus({
        pendingStatus: "completed",
        statusList: topicStatus,
      });
    } catch (error) {
      let errorDetails: ReactNode;
      if (
        error instanceof ApiResponseError &&
        isBcfBackendApiErrorDetails(error.body)
      ) {
        errorDetails = (
          <ExternalAnnotationError
            integrationType={externalAnnotationType}
            errorDetails={error.body}
            onAuthorizationButtonClicked={() =>
              Analytics.track<AuthorizeExternalAnnotationProperties>(
                EventType.authorizeExternalAnnotation,
                {
                  via: "annotations filter",
                  service: getExternalAnnotationProviderName(
                    externalAnnotationType,
                  ),
                },
              )
            }
          />
        );
      } else {
        errorDetails = parseErrorMessage(error);
      }
      setProjectExtensionsStatus({
        pendingStatus: "error",
        statusList: [],
        error: errorDetails,
      });
    }
  }, [bcfServicesApiClient, externalAnnotationType, projectId]);

  // Get the project extensions when the component mounts or when externalAnnotationType changes
  useEffect(() => {
    getProjectExtensions();
  }, [getProjectExtensions]);

  return useMemo(() => {
    // get the optional header first
    const providerName = getProviderName(externalAnnotationType);
    switch (projectExtensionsStatus.pendingStatus) {
      case "completed":
        return projectExtensionsStatus.statusList.length
          ? [
              getHeaderComponent(providerName),
              ...projectExtensionsStatus.statusList.map(
                (status): FilterMenuOptionType =>
                  getOptionComponent(
                    status.id,
                    status.name,
                    undefined,
                    externalAnnotationType,
                  ),
              ),
            ]
          : [];

      case "pending":
        return [
          getHeaderComponent(providerName),
          {
            key: getKeyForHybridStatus("pending", externalAnnotationType),
            label: <Skeleton variant="text" />,
            value: getKeyForHybridStatus("pending", externalAnnotationType),
          },
        ];

      case "error":
        // Return empty list = no additional feedback to the user.
        // Connection issue will be reported somewhere else in the app.
        return [];
    }
  }, [projectExtensionsStatus, externalAnnotationType]);
}

/** @returns A chip with a popup that can be used to select a list of users */
export function StatusFilter({
  selectedStatus,
  onSelectedStatusChanged,
}: AnnotationStatusFilterProps): JSX.Element {
  const optionsSphereXG = useMemo<FilterMenuOptionType[]>(
    () =>
      [
        AnnotationStatus.Unclassified,
        AnnotationStatus.Open,
        AnnotationStatus.InProgress,
        AnnotationStatus.ToReview,
        AnnotationStatus.Resolved,
      ].map((s) => getOptionComponent(s, FaroStatusText[s], s)),
    [],
  );

  const optionsAccIssues = useGetExternalStatusList(
    BcfServicesIntegrationType.autodeskAccIssues,
  );
  const optionsAccRfis = useGetExternalStatusList(
    BcfServicesIntegrationType.autodeskAccRfis,
  );
  const optionsBim360ISsues = useGetExternalStatusList(
    BcfServicesIntegrationType.autodeskBim360Issues,
  );
  const optionsBim360Rfis = useGetExternalStatusList(
    BcfServicesIntegrationType.autodeskBim360Rfis,
  );
  const optionsProcoreObservations = useGetExternalStatusList(
    BcfServicesIntegrationType.procoreObservations,
  );
  const optionsProcoreRfis = useGetExternalStatusList(
    BcfServicesIntegrationType.procoreRfis,
  );

  const options = useMemo<FilterMenuOptionType[]>(
    () => [
      ...optionsSphereXG,
      ...optionsAccIssues,
      ...optionsAccRfis,
      ...optionsBim360ISsues,
      ...optionsBim360Rfis,
      ...optionsProcoreObservations,
      ...optionsProcoreRfis,
    ],
    [
      optionsSphereXG,
      optionsAccIssues,
      optionsAccRfis,
      optionsBim360ISsues,
      optionsBim360Rfis,
      optionsProcoreObservations,
      optionsProcoreRfis,
    ],
  );

  const selectedOptions = useMemo<FilterMenuOptionType[]>(
    () =>
      selectedStatus
        .map((status) => options.find((option) => option.key === status))
        .filter((option) => !!option),
    [selectedStatus, options],
  );

  return (
    <ChipFilterWithSearch
      label="Status"
      selectedOptions={selectedOptions}
      onFilterChange={(selected) =>
        onSelectedStatusChanged(selected.map((option) => option.key))
      }
      options={options}
      headingLabel="Select statuses"
    />
  );
}

type StatusIconProps = {
  /** The status to represent */
  status: AnnotationStatus;
};

type Colors = {
  /** The color of the outer border */
  border: string;
  /** The color of the background of the icon */
  background: string;
  /** The color of the central dot */
  dot: string;
};

// TODO: Consolidate colors with chip-status (https://faro01.atlassian.net/browse/SWEB-5640)
/** The color scheme based on the status */
const STATUS_ICON_COLORS: Record<AnnotationStatus, Colors> = {
  [AnnotationStatus.Open]: {
    border: grey[300],
    background: grey[100],
    dot: grey[700],
  },
  [AnnotationStatus.InProgress]: {
    border: blue[100],
    background: blue[50],
    dot: blue[500],
  },
  [AnnotationStatus.ToReview]: {
    border: yellow[100],
    background: yellow[50],
    dot: yellow[500],
  },
  [AnnotationStatus.Resolved]: {
    border: green[100],
    background: green[50],
    dot: green[700],
  },
  [AnnotationStatus.Unclassified]: {
    border: grey[300],
    background: grey[0],
    dot: grey[500],
  },
};

/** @returns A colored icon to represent a specific status */
function StatusIcon({ status }: StatusIconProps): JSX.Element {
  return (
    <div
      style={{
        width: "24px",
        height: "24px",
        borderRadius: "12px",
        outlineColor: STATUS_ICON_COLORS[status].border,
        outlineWidth: "1px",
        outlineStyle: "solid",
        outlineOffset: -1,
        backgroundColor: STATUS_ICON_COLORS[status].background,
        padding: "8px",
        overflow: "visible",
      }}
    >
      <div
        style={{
          width: "8px",
          height: "8px",
          borderRadius: "4px",
          backgroundColor: STATUS_ICON_COLORS[status].dot,
        }}
      />
    </div>
  );
}

/**
 * @returns the header component for the status for a specific type of
 * @param name displayed name of the type
 */
function getHeaderComponent(name: string): FilterMenuOptionTypeWithHeader {
  return {
    isHeader: true,
    key: `Header${name}`,
    value: "",
    label: name,
  };
}

/**
 * @returns the Option component for a specific status of a third party provider
 * @param id id of the element in the list (must be unique)
 * @param displayName displayed name of the element in the list (may not be unique)
 * @param status value of the SphereXG status, or undefined for third party provider
 * @param externalAnnotationType type of third party provider, or undefined for SphereXG issues
 */
function getOptionComponent(
  id: string,
  displayName: string,
  status?: AnnotationStatus,
  externalAnnotationType?: BcfServicesIntegrationType,
): FilterMenuOptionType {
  return {
    key: getKeyForHybridStatus(id, externalAnnotationType),
    value: displayName,
    label: (
      <Stack direction="row" sx={{ gap: 0.5, alignItems: "center" }}>
        {status !== undefined && <StatusIcon status={status} />}
        <FaroText
          variant="bodyS"
          className={status === undefined ? NO_TRANSLATE_CLASS : undefined}
        >
          {displayName}
        </FaroText>
      </Stack>
    ),
    tooltip: status !== undefined && (
      <Stack direction="row" sx={{ gap: 0.5, alignItems: "center" }}>
        <StatusIcon status={status} />
        <FaroText variant="bodyS" dark>
          {displayName}
        </FaroText>
      </Stack>
    ),
  };
}
